import React from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { CurrencyEnums, DialogEnums } from "models/enums";
import { InvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { DialogProps } from "models/propTypes/dialogPropTypes";
//#endregion
//#region components
import InvoicePreview from "./preview";
import { RouteBreadCrumb } from "components/common";
import { useInvoiceService } from "serviceHooks";
import useGetPreviewDefaults from "./forms/preview/usePreviewDefaults";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

function useInvoicePreview() {
  const invoiceService = useInvoiceService();
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const {
    getCreateInvoicePreviewFormDefaultValues,
    getReminderInvoicePreviewFormDefaultValues,
    getInvoicePreviewFormDefaultValues,
  } = useGetPreviewDefaults();

  const getInvoiceProps = (invoice: WintSuperkollAPIModel.Invoice) => ({
    invoiceId: invoice.id,
    serialNumber: invoice.serialNumber,
    ocr: invoice.ocrText,
    status: invoice.status,
    customerName: invoice.customerName ?? "",
    canSendLastVersionToPagero: invoice.canSendLastVersionToPagero,
    warnings: invoice.warnings,
    fromRecurring: invoice.fromRecurring,
    recurringInvoiceVersion: invoice.recurringInvoiceVersion,
    recurringId: invoice.recurringId,
    total: invoice.leftToPay ?? invoice.totalAmount ?? 0,
    tax: invoice.totalTax ?? 0,
    currency: invoice.currency ?? CurrencyEnums.CurrencyCode.SEK,
    paymentTerms: invoice.paymentTerms ?? 0,
    invoiceRows: invoice.rows ?? [],
  });

  const getCreateInvoicePreviewDialog = (
    formValues: InvoiceFormTypes.Create,
    invoice?: WintSuperkollAPIModel.Invoice,
    invoicePut?: WintSuperkollAPIModel.InvoicePut,
    reloadInvoice?: (invoiceId: number) => void
  ): DialogProps => {
    const invoiceProps = invoice ? getInvoiceProps(invoice) : {};
    return {
      isOpen: false,
      title: (
        <RouteBreadCrumb
          current={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReviewInvoice)}
          showHome
        />
      ),
      dialogType: DialogEnums.DialogType.FullScreen,
      setDialogRef: true,
      children: (
        <InvoicePreview
          {...invoiceProps}
          invoicePut={invoicePut}
          status={WintSuperkollAPIModel.InvoiceStatus.NotSent}
          customerName={formValues.customer?.value ?? ""}
          reloadInvoice={reloadInvoice}
          total={
            (formValues.products ?? [])
              .map(
                (p) =>
                  invoiceService.calculateAmount(p.price ?? 0, p.quantity) +
                  invoiceService.calculateTax(p.price ?? 0, p.tax, p.quantity)
              )
              .reduce((a, b) => +a + +b, 0) + (invoice?.totalReminderCost ?? 0)
          }
          tax={(formValues.products ?? [])
            .map((p) => invoiceService.calculateTax(p.price ?? 0, p.tax, p.quantity))
            .reduce((a, b) => +a + +b, 0)}
          currency={formValues.currency ?? CurrencyEnums.CurrencyCode.SEK}
          paymentTerms={formValues.otherSettings.paymentTerms ?? 0}
          invoiceRows={formValues.products ?? []}
          initialValues={getCreateInvoicePreviewFormDefaultValues(formValues, invoice, {
            creditedInvoiceId: invoicePut?.creditedInvoiceId,
          })}
        />
      ),
      maxWidth: "sm",
    };
  };

  const getRemiderPreviewDialog = (
    invoice: WintSuperkollAPIModel.Invoice,
    reloadInvoice?: (invoiceId: number) => void,
    overrideTotalAmount?: number
  ): DialogProps => {
    const invoiceProps = getInvoiceProps(invoice);
    return {
      isOpen: false,
      title: (
        <RouteBreadCrumb
          current={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReviewInvoice)}
          showHome
        />
      ),
      dialogType: DialogEnums.DialogType.FullScreen,
      setDialogRef: true,
      children: (
        <InvoicePreview
          {...invoiceProps}
          reloadInvoice={reloadInvoice}
          total={overrideTotalAmount ?? invoiceProps.total}
          isReminder
          initialValues={getReminderInvoicePreviewFormDefaultValues(invoice)}
        />
      ),
      maxWidth: "sm",
    };
  };

  const getSentInvoicePreviewDialog = (
    invoice: WintSuperkollAPIModel.Invoice,
    invoicePut?: WintSuperkollAPIModel.InvoicePut,
    reloadInvoice?: (invoiceId: number) => void,
    defaultSendType?: WintSuperkollAPIModel.InvoiceSendMethod,
    version?: number,
    overrideTotalAmount?: number
  ): DialogProps => {
    const invoiceProps = getInvoiceProps(invoice);

    return {
      isOpen: false,
      title: (
        <RouteBreadCrumb
          current={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReviewInvoice)}
          showHome
        />
      ),
      dialogType: DialogEnums.DialogType.FullScreen,
      setDialogRef: true,
      children: (
        <InvoicePreview
          {...invoiceProps}
          invoicePut={invoicePut}
          reloadInvoice={reloadInvoice}
          total={overrideTotalAmount ?? invoiceProps.total}
          version={version}
          initialValues={getInvoicePreviewFormDefaultValues(invoice, {
            defaultSendType,
            version,
          })}
        />
      ),
      maxWidth: "sm",
    };
  };

  const showPreviewDialog = async (
    invoice?: WintSuperkollAPIModel.Invoice,
    invoicePut?: WintSuperkollAPIModel.InvoicePut,
    formValues?: InvoiceFormTypes.Create,
    reloadInvoice?: (invoiceId: number) => void,
    defaultSendType?: WintSuperkollAPIModel.InvoiceSendMethod,
    isReminder?: boolean,
    version?: number,
    overrideTotalAmount?: number
  ) => {
    let dialog: DialogProps;

    if (formValues) {
      dialog = getCreateInvoicePreviewDialog(formValues, invoice, invoicePut, reloadInvoice);
      dispatch(displayDialog(dialog));
      return;
    }

    if (!invoice) {
      return;
    }

    if (isReminder) {
      dialog = getRemiderPreviewDialog(invoice, reloadInvoice, overrideTotalAmount);
    } else {
      dialog = getSentInvoicePreviewDialog(
        invoice,
        invoicePut,
        reloadInvoice,
        defaultSendType,
        version,
        overrideTotalAmount
      );
    }

    dispatch(displayDialog(dialog));
  };

  return { getSentInvoicePreviewDialog, showPreviewDialog };
}

export default useInvoicePreview;
